import React from 'react';
import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';
import { parseStatusColorConfig } from '../../utils/statusColor';

export interface Appointment {
  clientAppId: number;
  clientId?: number;
  assignedTo?: number;
  clientAppName?: string;
  clientAppScheduleDateTime?: string;
  clientAppDurationMins?: number;
  clientAppFinishDateTime?: string;
  clientAppDuration?: number;
  clientAppNotes?: string;
  clientAppStatus?: number;
  clientAppOutcome?: string;
  appAssignedReminder?: number;
  appIsPrivate?: boolean;
  inviteEmails?: string[];
  clientAppCreatedAt?: string;
  clientAppCreatedBy?: number;
  clientAppUpdatedAt?: string;
  clientAppUpdatedBy?: number;
  // Related data from backend
  client?: {
    clientId: number;
    clientName?: string;
    contactPoints?: import('../address-book/contactPoints').ContactPoint[];
  };
  assignedToUser?: {
    id: number;
    userFirstName?: string;
    userDisplayName?: string;
  };
  clientAppDurationDescr?: {
    id: number;
    pd_descr?: string;
  };
  clientAppStatusDescr?: {
    id: number;
    as_descr?: string;
    as_color?: string;
  };
  FileAttachment?: Array<{
    id: number;
    fileName: string;
    filePath: string;
    fileType: string;
    fileSize: number;
    uploadedAt?: string;
  }>;
}

export interface AppointmentsResponse {
  records: Appointment[];
  openedCount?: number;
  overdueCount?: number;
  missingOutcomeCount?: number;
}

export const useAppointments = (searchParams?: string) => {
  const queryString = searchParams ? `?searchParams=${encodeURIComponent(searchParams)}` : '';
  const { data, error, isLoading, mutate } = useSWR<AppointmentsResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  // Extract records array from response
  const appointments = data?.records || [];

  return {
    data: appointments,
    appointments: appointments,
    total: appointments.length,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useAppointment = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Appointment>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    appointment: data,
    data: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get users for select dropdown
export const useUsersForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/users',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    users: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((user) => ({
      value: String(user.id),
      label: user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || `User #${user.id}`,
    })),
  };
};

// Hook to get durations for select dropdown (sorted by pd_order)
export const sortDurationsByOrder = <T extends { pd_order?: number; pd_min?: number }>(
  items: T[],
): T[] =>
  [...items].sort(
    (a, b) => (a.pd_order ?? 0) - (b.pd_order ?? 0) || (a.pd_min ?? 0) - (b.pd_min ?? 0),
  );

export const useDurationsForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-duration',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const durations = sortDurationsByOrder(data || []);

  // Expose a global duration map (id -> minutes) for generic field renderers
  if (typeof window !== 'undefined' && durations.length > 0) {
    const map: Record<string, number> = {};
    for (const d of durations) {
      if (d && d.id != null && typeof d.pd_min === 'number') {
        map[String(d.id)] = d.pd_min;
      }
    }
    (window as any).__pcrmDurationMinutesMap = map;
  }

  return {
    durations,
    isLoading,
    isError: error,
    options: durations.map((duration) => ({
      value: String(duration.id),
      label: duration.pd_descr || `Duration #${duration.id}`,
    })),
  };
};

// Hook to get appointment statuses for select dropdown
export const useAppStatusesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/p-crm-app-status',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    statuses: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((status) => ({
      value: String(status.id),
      label: status.as_descr || `Status #${status.id}`,
      icon: React.createElement('span', {
        className: 'inline-block h-2.5 w-2.5 rounded-full border border-black/10',
        style: { backgroundColor: parseStatusColorConfig(status.as_color).backgroundColor },
      }),
    })),
  };
};

